import { formatMinor } from '@hvac/contracts';
import { Alert, Skeleton } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { jobQuery } from '../../shared/api/parties-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { AtAGlancePanel, ObjectTimeline, RelationshipsPanel } from '../objects/object-panels.js';

/**
 * One job. Quoted, invoiced, cost, margin, then the same two panels every
 * object gets. The margin is computed here from the two rollups, never stored
 * as its own number that could drift from them.
 */
export function JobDetailPage() {
  const { jobId } = useParams({ from: '/protected/jobs/$jobId' });
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const job = useQuery(jobQuery(organizationId, jobId));

  if (job.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (job.isError) {
    return (
      <Alert tone="danger" title="That job could not be loaded">
        {job.error.message}
      </Alert>
    );
  }

  const data = job.data;
  const jobMargin = BigInt(data.invoicedAmountMinor) - BigInt(data.costAmountMinor);
  const dates = [
    data.scheduledOn === null ? null : `scheduled ${data.scheduledOn}`,
    data.startedOn === null ? null : `started ${data.startedOn}`,
    data.completedOn === null ? null : `completed ${data.completedOn}`,
  ]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/jobs" className="text-sm text-content-muted hover:text-content">
          ← Jobs
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-content">
          {data.jobNumber === null ? data.title : `${data.jobNumber}, ${data.title}`}
        </h1>
        <p className="text-sm text-content-muted">
          {data.customerName === null ? '' : `${data.customerName} · `}
          {data.status.replaceAll('_', ' ')}
          {dates === '' ? '' : ` · ${dates}`}
          {data.isWarranty ? ' · warranty callback' : ''}
        </p>
      </div>

      {data.description === null ? null : (
        <p className="text-sm text-content-muted">{data.description}</p>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Quoted"
          value={data.quotedAmountMinor === null ? '-' : formatMinor(data.quotedAmountMinor)}
        />
        <Stat label="Invoiced" value={formatMinor(data.invoicedAmountMinor)} />
        <Stat label="Cost" value={formatMinor(data.costAmountMinor)} />
        <Stat label="Margin" value={formatMinor(jobMargin.toString())} />
      </div>

      <AtAGlancePanel organizationId={organizationId} entityType="job" entityId={data.id} />

      <RelationshipsPanel organizationId={organizationId} entityType="job" entityId={data.id} />

      <ObjectTimeline organizationId={organizationId} resourceType="job" resourceId={data.id} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface p-3">
      <p className="text-xs text-content-muted">{label}</p>
      <p className="tabular mt-0.5 text-base font-medium text-content">{value}</p>
    </div>
  );
}
